import React, { useState, useCallback } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import format from 'date-fns/format';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { StyledGrid } from 'styles/common';

import { Expand } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextBodyLarge } from 'uiToolkitV1/Typography';

import ExpandedCFBarChart from './ExpandedCFBarChart';

const CustomTooltip = ({ data, isTooltipActive }) => {
  const formattedTime = () => {
    const tooltipMonth = format(new Date(data?.time), 'MMMMMM');
    const tooltipYear = format(new Date(data?.time), 'yyyy');
    return (
      <TextBodyMedium color="#666666">
        {tooltipMonth}, {tooltipYear}
      </TextBodyMedium>
    );
  };

  if (isTooltipActive) {
    return (
      <Flex
        sx={{
          width: '155px',
          height: '104px',
          background: 'white',
          color: '#333',
          boxShadow: '0 3px 14px rgb(0 0 0 / 40%)',
          padding: '10px',
          borderRadius: '12px',
          border: '1px solid #F2F2F2',
        }}
      >
        <Box
          sx={{
            height: '74px',
            width: '4px',
            borderRadius: '4px',
            backgroundColor: data?.color,
          }}
        />
        <Flex
          flexDirection="column"
          justifyContent="space-evenly"
          sx={{
            paddingLeft: '10px',
            paddingTop: '10px',
          }}
        >
          <TextBodyMedium marginTop="-25px" bold>
            {data?.name}
          </TextBodyMedium>
          <Flex>
            <TextBodyLarge bold>{data?.count}</TextBodyLarge>
          </Flex>
          {formattedTime()}
        </Flex>
      </Flex>
    );
  }

  return null;
};

const renderLegendText = (value) => <span style={{ color: 'black' }}>{value}</span>;

export const PlotBarChart = ({ chartData }) => {
  const [tooltipData, setTooltipData] = useState({
    count: null,
    time: null,
    name: null,
  });
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const dateFormatter = (date) => format(new Date(date), 'MMM');

  return (
    <ResponsiveContainer height="100%" minHeight="280px" width="100%">
      <BarChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
        barSize={30}
        barGap={0}
        cursor="pointer"
      >
        <CartesianGrid />
        <XAxis
          dataKey="KEY"
          tickLine={false}
          tick={{ fill: '#666666', fontSize: 10 }}
          label={{ value: 'Time (Months) →', position: 'insideBottom', fill: '#666666' }}
          height={40}
          tickFormatter={dateFormatter}
        />
        <YAxis
          tickLine={false}
          tick={{ fill: '#666666', fontSize: 10 }}
          label={{
            value: 'No. of feedback →',
            angle: -90,
            position: 'insideLeft',
            fill: '#666666',
            style: { textAnchor: 'middle' },
          }}
          width={40}
        />
        <Tooltip cursor={false} content={<CustomTooltip data={tooltipData} isTooltipActive={isTooltipActive} />} />
        <Legend iconType="circle" verticalAlign="bottom" align="right" formatter={renderLegendText} />
        <Bar
          dataKey="DETAILS.sharedPraise.feedbackCount"
          fill="#91CA7D"
          stackId="a"
          name="Praises Shared"
          onMouseOver={(data) => {
            const name = data?.tooltipPayload?.[0]?.value > 1 ? 'Praises Shared' : 'Praise Shared';
            setIsTooltipActive(true);
            setTooltipData({
              count: data?.DETAILS?.sharedPraise?.feedbackCount,
              time: data?.KEY,
              name,
              color: data?.tooltipPayload[0].color,
            });
          }}
          onMouseOut={() => {
            setIsTooltipActive(false);
            setTooltipData({
              count: null,
              time: null,
              name: null,
            });
          }}
        >
          <LabelList
            dataKey="DETAILS.sharedPraise.feedbackCount"
            position="insideTop"
            fill="#333333"
            fontSize={10}
            formatter={(label) => (label >= 20 ? label : null)}
          />
        </Bar>

        <Bar
          dataKey="DETAILS.receivedPraise.feedbackCount"
          fill="#DBF2D3"
          stackId="a"
          name="Praises Received"
          onMouseOver={(data) => {
            const name = data?.tooltipPayload?.[0]?.value > 1 ? 'Praises Received' : 'Praise Received';
            setIsTooltipActive(true);
            setTooltipData({
              count: data?.DETAILS?.receivedPraise?.feedbackCount,
              time: data?.KEY,
              name,
              color: data?.tooltipPayload[0].color,
            });
          }}
          onMouseOut={() => {
            setIsTooltipActive(false);
            setTooltipData({
              count: null,
              time: null,
              name: null,
            });
          }}
        >
          <LabelList
            dataKey="DETAILS.receivedPraise.feedbackCount"
            position="insideTop"
            fill="#333333"
            fontSize={10}
            formatter={(label) => (label >= 20 ? label : null)}
          />
        </Bar>

        <Bar
          dataKey="DETAILS.sharedAdvice.feedbackCount"
          fill="#FFDE88"
          stackId="b"
          name="Feedback Shared"
          onMouseOver={(data) => {
            const name = 'Feedback Shared';
            setIsTooltipActive(true);
            setTooltipData({
              count: data?.DETAILS?.sharedAdvice?.feedbackCount,
              time: data?.KEY,
              name,
              color: data?.tooltipPayload[0].color,
            });
          }}
          onMouseOut={() => {
            setIsTooltipActive(false);
            setTooltipData({
              count: null,
              time: null,
            });
          }}
        >
          <LabelList
            dataKey="DETAILS.sharedAdvice.feedbackCount"
            position="insideTop"
            fill="#333333"
            fontSize={10}
            formatter={(label) => (label >= 20 ? label : null)}
          />
        </Bar>
        <Bar
          dataKey="DETAILS.receivedAdvice.feedbackCount"
          fill="#FFEFD5"
          stackId="b"
          name="Feedback Received"
          onMouseOver={(data) => {
            const name = 'Feedback Received';
            setIsTooltipActive(true);
            setTooltipData({
              count: data?.DETAILS?.receivedAdvice?.feedbackCount,
              time: data?.KEY,
              name,
              color: data?.tooltipPayload[0].color,
            });
          }}
          onMouseOut={() => {
            setIsTooltipActive(false);
            setTooltipData({
              count: null,
              time: null,
              name: null,
            });
          }}
        >
          <LabelList
            dataKey="DETAILS.receivedAdvice.feedbackCount"
            position="insideTop"
            fill="#333333"
            fontSize={10}
            formatter={(label) => (label >= 20 ? label : null)}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

function CFbarChart({ chartData }) {
  const [isExpandedViewOpen, setIsExpandedView] = useState(false);

  const onClose = () => {
    setIsExpandedView(false);
  };

  const onExpand = useCallback(() => {
    setIsExpandedView(true);
  }, [setIsExpandedView]);

  return (
    <Flex flexDirection="column">
      <StyledGrid container justifyContent="space-between" alignItems="center">
        <TextBodyMedium bold mb={4}>
          Feedback shared and received
        </TextBodyMedium>
        <Button variant="tertiary" EndIcon={Expand} onClick={onExpand} />
      </StyledGrid>
      <PlotBarChart chartData={chartData} />
      <ExpandedCFBarChart isOpen={isExpandedViewOpen} onClose={onClose}>
        <PlotBarChart chartData={chartData} />
      </ExpandedCFBarChart>
    </Flex>
  );
}

export default CFbarChart;
